import React from "react";
import { X } from "lucide-react";

type Tone = "success" | "danger" | "warn" | "info" | "muted" | "accent";

const toneClass: Record<Tone, string> = {
  success: "alert-success",
  danger: "alert-danger",
  warn: "alert-warn",
  info: "alert-info",
  muted: "alert-muted",
  accent: "alert-success"
};

type AlertProps = {
  tone?: Tone;
  title?: string;
  icon?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  /** Optional dismiss control (e.g. minimize). Renders an X in the top-right. */
  onClose?: () => void;
  closeLabel?: string;
};

export const Alert: React.FC<AlertProps> = ({
  tone = "info",
  title,
  icon,
  className = "",
  children,
  onClose,
  closeLabel = "Dismiss"
}) => (
  <div className={`${toneClass[tone]} ${className}`.trim()} role="status">
    <div className="flex items-start gap-3">
      {icon && <div className="mt-0.5 shrink-0">{icon}</div>}
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={title ? "mt-1" : ""}>{children}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          className="-mr-1 -mt-1 shrink-0 self-start rounded p-1 opacity-70 hover:opacity-100"
          onClick={onClose}
          aria-label={closeLabel}
        >
          <X size={16} />
        </button>
      )}
    </div>
  </div>
);

const badgeTone: Record<Tone, string> = {
  success: "badge-success",
  danger: "badge-danger",
  warn: "badge-warn",
  info: "badge-info",
  muted: "badge-muted",
  accent: "badge-accent"
};

export const Badge: React.FC<{ tone?: Tone; className?: string; children: React.ReactNode }> = ({
  tone = "muted",
  className = "",
  children
}) => <span className={`${badgeTone[tone]} ${className}`.trim()}>{children}</span>;

export const Spinner: React.FC<{ className?: string }> = ({ className = "" }) => (
  <span
    className={`inline-block h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent ${className}`}
    aria-label="Loading"
  />
);

export const Panel: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = "",
  children
}) => <div className={`panel p-5 ${className}`.trim()}>{children}</div>;

export const PageHeader: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, subtitle, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div>
      <h1 className="page-title">{title}</h1>
      {subtitle && <div className="page-subtitle">{subtitle}</div>}
    </div>
    {actions}
  </div>
);
